import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => {
      setPosts(r.data.posts);
      setLoading(false);
    });
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "BreezeBlog",
    "description": "Fragrance, car culture, and modern lifestyle from the Booty Breeze editors.",
    "url": "https://bootybreeze.com/blog",
    "blogPost": posts.slice(0, 20).map((p) => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "url": `https://bootybreeze.com/blog/${p.slug}`,
      "datePublished": p.published_at,
      "author": { "@type": "Person", "name": p.author || "Bree" },
      "keywords": (p.tags || []).join(", "),
    })),
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12" data-testid="blog-list-page">
      <SEO
        title="BreezeBlog — Fragrance, Cars & Culture"
        description="Fragrance, car culture, novelty accessories, and the aesthetic of driving in 2026. Fresh writing from Booty Breeze, updated daily."
        path="/blog"
        jsonLd={jsonLd}
      />
      <div className="mb-12">
        <div className="inline-flex items-center gap-2 text-[#A855F7] uppercase text-xs tracking-widest mb-3">
          <Sparkles size={12} /> Daily reading
        </div>
        <h1 className="font-display text-5xl sm:text-7xl text-white leading-none">
          Breeze<span className="text-neon-pink">Blog</span>
        </h1>
        <p className="text-white/60 mt-4 max-w-xl text-sm sm:text-base leading-relaxed">
          Fragrance, car culture, and the everyday aesthetic. Fresh writing from Bree — our resident tastemaker — every single day.
        </p>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm">Loading posts…</div>
      ) : posts.length === 0 ? (
        <div className="text-white/40 text-sm">No posts yet. Check back tomorrow morning.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, idx) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, duration: 0.5 }}
              className="group"
            >
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="block rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden hover:border-[#FF3D8B] hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(255,61,139,0.25)] transition-[transform,border-color,box-shadow] duration-300"
              >
                {p.image_url && (
                  <div className="aspect-[16/9] bg-black overflow-hidden">
                    <img
                      src={resolveImage(p.image_url)}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(p.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 text-[#A855F7]">{t}</span>
                    ))}
                  </div>
                  <h2 className="font-display text-xl text-white group-hover:text-[#FF3D8B] transition-colors leading-tight">
                    {p.title}
                  </h2>
                  <p className="text-white/50 text-sm leading-relaxed mt-2 line-clamp-3">{p.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#FF3D8B] group-hover:gap-2.5 transition-all">
                    Read more <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      )}
    </div>
  );
}
